import { useDispatch, useSelector } from "react-redux";
import { useProposalTypeData } from "../../../../hooks/proposal/use-proposal-type-data";
import { useRiderValidation } from "../../../../hooks/validations/use-riders-value-validation";
import { useFirstStepValidation } from "../../../../hooks/validations/use-first-step-validation";
import { setUpdateTypeModal, } from "../../../../features/gms-state-and-modal/modal-slice";
import { useSecondStepValidation } from "../../../../hooks/validations/use-second-step-validation";
import { useIsRequiredFileValidation } from "../../../../hooks/validations/use-is-required-file-validation";
import { useInputsRidersValidation } from "../../../../hooks/validations/use-inputs-riders-validation";
import { generateRemainingAgeRate } from "../../../../utils/generate-remaining-age.utils";
import { useCoverageInputsValidation } from "../../../../hooks/validations/user-coverage-inputs-validation";
import { useCreateProposalForm } from "../../../../hooks/actions/use-create-proposal-form";
import { addBorrowerAgeOption, setBorrowerAgeFromAPI, } from "../../../../features/gms-proposal-forms/age-bracket-and-benefit-amount/age-bracket-and-benefit-amount.slice";
import { selectProposalCurrentGroupName, setCurrentStep, } from "../../../../features/gms-proposal-forms/steps-channel-proposal_as/steps-channel-proposal_as.slice";
import { useToastify } from "../../../../library/toastify/toast-provider";
import { useModalAccess } from "../../../global/modal/use-modal-access";

interface Props {
    handleSaveClientData: (typeBtn: "draft" | "final") => void;
    hasDraft: boolean;
    isRenewal?: boolean;
}

export default function HeaderProgress({ handleSaveClientData, hasDraft, isRenewal = false }: Props) {
    
    const { showToastNotify } = useToastify();
    const dispatch = useDispatch();
    const {
        validateChannel,
        validProposalAs,
        validBasicInformation,
        validAffiliation,
        validGroupClassification,
        validTypeOfGroup,
        validateAge,
        validModeOfPayment,
        isLoadingExistingGroupName,
        validGroupName,
    } = useFirstStepValidation();
    const { isUpdateProposal } = useModalAccess()
    const { debouncedGroupName, existingCompany } = useCreateProposalForm();
    const { isValidSecondStep } = useSecondStepValidation();
    const { validateAll } = useRiderValidation();
    const { 
        isPrototype, 
        groupName: currentGroupName,
        maxAgeProfile: maxAge,
        minAgeProfile: minAge,
        proposalStep: currentStep,
        isPendingCurrentProposalStatus,
        currentStatusName
    } = useProposalTypeData();
    const { isHasAttachedFile } = useIsRequiredFileValidation();
    const { isValidSelectedRiders } = useInputsRidersValidation();
    const { isValidCoverageInputs } = useCoverageInputsValidation();
    const isFirstStep = currentStep === 1;
    const isLastStep = currentStep === 4;
    
    const originalGroupName = useSelector(selectProposalCurrentGroupName) ?? "";
    
    const normalizeGroupName = (value: string) => value.trim().replace(/\s+/g, " ").toLowerCase();

    const isGroupNameChanged = normalizeGroupName(originalGroupName) !== normalizeGroupName(currentGroupName);

    const shouldValidateGroupName = !!debouncedGroupName && (!isUpdateProposal || isGroupNameChanged);

    const onNextClick = async () => {
        if (currentStep === 1) {
            /*
            * GROUP NAME DUPLICATE VALIDATION
            *
            * Create:
            *   Always validate the group name.
            *
            * Update:
            *   If the group name was not changed,
            *   allow the existing name because it belongs
            *   to the current proposal.
            *
            *   If the group name was changed,
            *   validate the new name.
            */
            if (shouldValidateGroupName && existingCompany?.data.exists === true) {
                showToastNotify("Group name already exists.", "warning");
                return;
            }
            if (!validateChannel()) return;
            if (!validProposalAs()) return;
            if (!validBasicInformation()) return;
            if (!validAffiliation()) return;
            if (!validGroupClassification()) return;
            if (!validTypeOfGroup()) return;
            if (!validateAge()) return;
            if (!validModeOfPayment()) return;
            // if (!validGroupName()) return;
            if (!minAge || !maxAge) return;
            const remainingAge = generateRemainingAgeRate(minAge, maxAge);

            if (maxAge !== 65) {
                dispatch(
                    setBorrowerAgeFromAPI({
                        type: "check",
                        value: {
                            borrower_age_66_70: false,
                            borrower_age_71_75: false,
                            borrower_age_76_80: false,
                        },
                    })
                );

                dispatch(
                    setBorrowerAgeFromAPI({
                        type: "amount",
                        fAmount: {
                            borrower_amount_66_70: 0,
                            borrower_amount_71_75: 0,
                            borrower_amount_76_80: 0,
                        },
                    })
                );
            }

            remainingAge.forEach((item) => {
                dispatch(
                    addBorrowerAgeOption({
                        id: item.id,
                        label: item.label,
                        typeOfAge: item.typeOfAge,
                        type: "rm",
                    })
                );
            });
        }

        if (currentStep === 2) {
            const isValid = isValidCoverageInputs();
            if (!isValid) return;
            if (!isValidSecondStep()) return;
            if (!isHasAttachedFile()) return;
            if (!isValidSelectedRiders()) return;
        }

        if (!validateAll()) return;
        goToNextStep();
    };

    const goToPreviousStep = () => {
        if (isPrototype && currentStep === 4) {
            dispatch(setCurrentStep(2));
            return;
        }
        if (isPrototype && currentStep === 2) {
            dispatch(setCurrentStep(1));
            return;
        }
        if (currentStep > 1) {
            dispatch(
                setCurrentStep(
                    (currentStep - 1) as 1 | 2 | 3 | 4
                )
            );
        }
    };

    const goToNextStep = () => {
        if (currentStep < 4) {
            dispatch(setCurrentStep((currentStep + 1) as 1 | 2 | 3 | 4));
        }
        if (isPrototype && currentStep === 2) {
            dispatch(setCurrentStep(4));
            return;
        }
    };

    const handleSaveDraft = async () => {
        if (!validateChannel()) return;
        if (!validBasicInformation()) return;
        if (!validGroupClassification()) return;
        if (!validTypeOfGroup()) return;
        if (!validateAge()) return;
        if (!validModeOfPayment()) return;
        if (!validGroupName()) return;

        handleSaveClientData("draft");

        if (isUpdateProposal) {
            return;
        }

        dispatch(setUpdateTypeModal("submitAsDraftProposal"));
    };

    const stepTitle =
        currentStep === 1
            ? "Group Information"
            : currentStep === 2
                ? "Insurance Plans & Riders"
                : currentStep === 3
                    ? "Notes & Supporting Documents"
                    : "Review & Save";

    const stepDescription =
        currentStep === 1
            ? "Fill out all required information before proceeding."
            : currentStep === 2
                ? "Review and choose suitable coverage options."
                : currentStep === 3
                    ? "Add notes and attach any supporting documents required for the proposal."
                    : "Review all details and save the information.";

    return (
        <div className="flex flex-col gap-3 bg-white">
            {/* STEP HEADER */}
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                {/* CURRENT STEP */}
                <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#186cb2] text-sm font-bold text-white shadow-sm">
                        {currentStep}
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#186cb2]">
                                Step {currentStep} of 4
                            </p>
                        </div>
                        <h2 className="truncate text-sm font-bold text-gray-900">
                            {stepTitle}
                        </h2>
                        <p className="hidden text-[11px] text-gray-500 sm:block">
                            {stepDescription}
                        </p>
                    </div>
                </div>
                {/* ACTION BUTTONS */}
                <div className="grid grid-cols-3 gap-2 sm:flex sm:items-center">
                    {/* BACK */}
                    <button
                        type="button"
                        disabled={isFirstStep}
                        onClick={goToPreviousStep}
                        className={`flex items-center justify-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-semibold transition ${isFirstStep ? "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400" : "border-gray-200 bg-white text-gray-600 hover:border-[#186cb2]/40 hover:bg-[#186cb2]/5 hover:text-[#186cb2]"}`}
                    >
                        <span className="text-sm">←</span>
                        <span>Back</span>
                    </button>
                    {/* DRAFT */}
                    {hasDraft && (
                        <button
                            type="button"
                            onClick={handleSaveDraft}
                            disabled={isUpdateProposal ? isPendingCurrentProposalStatus : false || !hasDraft}
                            // className={`${isDraftProposal ? 'hover:border-[#186cb2]/40 hover:bg-[#186cb2]/5 hover:text-[#186cb2]' : 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'} disabled:cursor-not-allowed flex items-center justify-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-semibold transition`}
                            className={`enabled:hover:border-[#186cb2]/40 enabled:hover:bg-[#186cb2]/5 enabled:hover:text-[#186cb2] border-gray-200 bg-gray-100 text-gray-400'} disabled:cursor-not-allowed flex items-center justify-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-semibold transition`}
                        >
                            <span>Draft</span>
                        </button>
                    )}
                    {/* NEXT */}
                    {(currentStep === 1 || currentStep === 2 || currentStep === 3) && (
                        <button
                            type="button"
                            onClick={onNextClick}
                            disabled={isLoadingExistingGroupName}
                            className={`flex items-center justify-center gap-1.5 rounded-xl px-4 py-2 text-xs font-semibold text-white shadow-sm transition ${isLoadingExistingGroupName ? "cursor-not-allowed bg-gray-300" : "bg-[#186cb2] hover:bg-[#155f9d] hover:shadow-md hover:shadow-[#186cb2]/20 active:scale-[0.99]"}`}
                        >
                            <span>Next</span>
                            <span className="text-sm">→</span>
                        </button>
                    )}
                    {/* SUBMIT */}
                    {isLastStep && (
                        <button
                            type="button"
                            onClick={() => handleSaveClientData("final")}
                            className="flex items-center justify-center gap-1.5 rounded-xl bg-[#186cb2] px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-[#155f9d] hover:shadow-md hover:shadow-[#186cb2]/20 active:scale-[0.99]"
                        >
                            <span>Submit</span>
                        </button>
                    )}
                </div>
            </div>
            {/* PROGRESS BAR */}
            <div className="flex items-center gap-1">
                {[1, 2, 3, 4].map((step) => {
                    const isCompleted = step < currentStep;
                    const isCurrent = step === currentStep;
                    return (
                        <div key={step} className="flex flex-1 items-center gap-1">
                            <div className={`h-1.5 w-full rounded-full transition-all duration-300 ${isCompleted || isCurrent ? "bg-[#186cb2]" : "bg-gray-200"}`} />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}